import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, MousePointerClick, Sliders, Download, CheckCircle2, Star, Package, Lightbulb, Puzzle, Palette, Wrench, ChevronDown, Globe, Layers, Eye, Cog, Heart, Volume2, Hammer, Megaphone, Shapes, Gem, Boxes, Box, Scissors, Ruler, PenTool, Grid3x3, Cpu, Sparkle, Wand2, Component } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { requiresPro, getToolTag, getToolTier, userTierAllows, visibleBoxTypes, subscribeToolTiers, getToolBadge, getToolBadgeLabel, isToolEnabled, isToolComingSoon, getToolCategory, isToolFeatured, getCategories, subscribeCategories, getCategoryLabel, FALLBACK_CATEGORY } from '../mock';

// Map a Lucide icon NAME (stored per-category in the DB) to its
// component. Unknown names fall back to a generic shapes icon so a
// custom category created with any icon string still renders.
const ICON_MAP = {
  Package, Lightbulb, Puzzle, Palette, Wrench, Volume2, Hammer, Megaphone,
  Shapes, Gem, Boxes, Box, Scissors, Ruler, PenTool, Grid3x3, Cpu,
  Sparkle, Wand2, Component, Layers, Cog, Star, Heart,
};
const iconFor = (name) => ICON_MAP[name] || Shapes;
import { useI18n } from '../i18n';
import { useAuth } from '../auth/AuthContext';
import BoxPreviewCard from '../components/BoxPreviewCard';
import HomeFaq, { buildHomeFaqJsonLd } from '../components/HomeFaq';
import Seo, { buildWebAppJsonLd, buildItemListJsonLd, buildOrganizationJsonLd } from '../seo/Seo';
import { toolPath } from '../lib/toolPath';

export default function Home() {
  const { lang, t } = useI18n();
  const { isAdmin } = useAuth();
  // Subscribe to tier-override updates so the grid re-filters the
  // moment /api/tools lands at boot. Without this, the first paint
  // happens before the override map is populated and draft tools
  // remain visible to anonymous visitors until the next navigation.
  const [, setTierTick] = useState(0);
  useEffect(() => subscribeToolTiers(() => setTierTick((n) => n + 1)), []);
  // Subscribe to category updates so the sections re-render the moment
  // /api/categories lands at boot (and after any admin edit in the same
  // session). Mirrors the tier-override subscription above.
  const [, setCatTick] = useState(0);
  useEffect(() => subscribeCategories(() => setCatTick((n) => n + 1)), []);
  // Draft tools are visible only to admins (QA before promoting to
  // basic/pro). Everyone else gets the catalogue with drafts removed.
  const boxes = visibleBoxTypes(isAdmin);

  // ── CATEGORIES ─────────────────────────────────────────────────────
  // Group tools by their `category` field (set in mock.js). The
  // ordering of CATEGORIES is the visual ordering on the page; each
  // entry has the i18n key prefix and a Lucide icon. The 'comingSoon'
  // bucket is ALWAYS rendered last and starts collapsed (<details>).
  // Each category carries a Tailwind colour token so the hero tabs,
  // the sticky chips and the section headers share a coherent accent.
  // The `preview` field is the public asset rendered in the hero
  // composition when the tab is selected (or always, on the stack).
  // Categories are now DYNAMIC (fetched from /api/categories at boot,
  // falling back to DEFAULT_CATEGORIES). The visual ordering is the
  // category sort_order from the backend; admins reorder/create/delete
  // them from the Admin panel without a redeploy. We resolve each
  // category's Lucide icon by name here.
  const CATEGORIES = useMemo(
    () => getCategories().map((c) => ({
      id: c.id,
      icon: iconFor(c.icon),
      accent: c.accent || 'cyan',
      preview: c.preview || '/preview/basic-box.webp',
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setCatTick],
  );
  // Resolved category ids in display order (for grouping + fallback).
  const catIds = useMemo(() => CATEGORIES.map((c) => c.id), [CATEGORIES]);
  const fallbackCat = catIds.includes(FALLBACK_CATEGORY) ? FALLBACK_CATEGORY : (catIds[0] || 'tools');

  // Tab currently highlighted in the hero composition. Defaults to the
  // first category that has at least one tool. The user can hover or
  // click to swap the featured preview without leaving the hero.
  const [heroTab, setHeroTab] = useState('boxes');
  // Keep heroTab valid if its category was removed/disabled.
  useEffect(() => {
    if (catIds.length && !catIds.includes(heroTab)) setHeroTab(catIds[0]);
  }, [catIds, heroTab]);

  // Group every visible tool into its category bucket. Coming-soon
  // tools (admin badge='soon' or catalogue disabled=true) go into a
  // dedicated bucket rendered at the bottom of the catalogue as a
  // muted strip — they appear in the grid but stay unclickable.
  // Category resolution: admin override wins over the catalogue
  // default, so admins can move a tool between categories from the
  // panel without a redeploy. Orphan tools (category deleted) are
  // re-homed to the fallback bucket by getToolCategory().
  const grouped = useMemo(() => {
    const acc = { comingSoon: [] };
    catIds.forEach((id) => { acc[id] = []; });
    boxes.forEach((b) => {
      const isSoon = b.disabled || isToolComingSoon(b.slug);
      if (isSoon) { acc.comingSoon.push(b); return; }
      const cat = getToolCategory(b.slug);
      (acc[cat] || acc[fallbackCat]).push(b);
    });
    // Featured tool (one only — backend enforces uniqueness) always
    // first inside its category. Same source-of-truth (override → catalogue).
    Object.keys(acc).forEach((k) => {
      acc[k].sort((a, b) => (isToolFeatured(b.slug) ? 1 : 0) - (isToolFeatured(a.slug) ? 1 : 0));
    });
    return acc;
  }, [boxes, catIds, fallbackCat]);
  // Safe accessor — a category may have no bucket during a transient
  // re-render right after a reorder.
  const bucket = (id) => grouped[id] || [];

  // Category title: prefer the dynamic DB label; for the 8 builtins
  // keep using the existing i18n keys (cat<Id>) when the DB label is
  // missing, so nothing regresses. Custom categories use their label.
  const catTitle = (id) => {
    const dbLabel = getCategoryLabel(id, lang);
    if (dbLabel && dbLabel !== id) return dbLabel;
    const key = `cat${id.charAt(0).toUpperCase() + id.slice(1)}`;
    const tr = t(key);
    return tr === key ? id : tr;
  };
  // Category one-liner: only builtins have i18n descriptions; custom
  // categories simply omit it (returns '').
  const catDesc = (id) => {
    const key = `cat${id.charAt(0).toUpperCase() + id.slice(1)}Desc`;
    const tr = t(key);
    return tr === key ? '' : tr;
  };

  // CTA: smooth-scroll to the templates grid (#templates) or to a
  // specific category section when the user clicks a navigation chip.
  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Seo
        titleKey="seoHomeTitle"
        descriptionKey="seoHomeDesc"
        path="/"
        keywords={[
          // SEO target keywords — kept in English because that's what
          // search volume is heaviest on, regardless of the user's UI
          // locale. The Helmet block emits these into <meta name="keywords">;
          // Google ignores it but Bing/Yandex/Baidu still read it, and
          // it also enriches the SoftwareApplication JSON-LD below.
          'parametric box generator',
          'laser cut generator',
          'dxf generator online',
          'svg generator online',
          'cnc design tool',
          'finger joint box generator',
          'laser ready files',
          'free dxf generator',
          'makercase alternative',
          'digital fabrication',
        ]}
        jsonLd={[
          buildWebAppJsonLd({
            name: t('seoSiteName'),
            description: t('seoHomeDesc'),
          }),
          // Organization payload — emitted ONLY here on the home page
          // (not per tool) so Google sees a single, authoritative
          // entity declaration. This is the strongest signal for
          // brand-typo resolution: `alternateName` teaches Google
          // that "Maker Cutt", "MakerCut" and "Maker Cut" are
          // synonyms of MakerCutt and resolve to this same
          // Knowledge Graph entity.
          buildOrganizationJsonLd(),
          // ItemList of the visible tool catalogue.
          //
          // Why this matters: telling Google the home page is an INDEX
          // of N tools (not a single document) dramatically increases
          // the chance the SERP entry shows sitelinks (the cluster of
          // small links under our main result) for the top tools. The
          // sitemap.xml alone is not enough — that signals discovery,
          // ItemList signals semantic intent.
          //
          // We only include `boxes` (visible, non-draft, non-coming-soon)
          // and cap at 30 entries (Google ignores larger lists anyway).
          buildItemListJsonLd({
            name: 'MakerCutt Tools',
            items: boxes.slice(0, 30).map((b) => ({
              name: (b.name && (b.name.en || b.name.pt)) || b.slug,
              url: toolPath(b.slug),
              description: b.desc && (b.desc.en || b.desc.pt),
            })),
          }),
          // Consolidated FAQ — the site has a single FAQ on the home page;
          // emitting the FAQPage payload here keeps the rich-result eligible.
          buildHomeFaqJsonLd(lang),
        ]}
      />
      <Header />
      <main className="flex-1 blueprint-grid">
        {/* ── PREMIUM DARK HERO ──────────────────────────────────────────
            Two-column hybrid hero (mock-up inspired):
            • LEFT: dark zinc-950 panel with a cyan/fuchsia glow,
              dotted blueprint grid, big chunky headline, sub copy,
              CTA + secondary CTA, 3 reassurance perks and a row of
              colored CATEGORY TABS (Caixas / Luminárias / Puzzles /
              Decorativos / Ferramentas) that swap the preview shown
              on the right.
            • RIGHT: a tilted preview "stack" — three thumbnails
              composed on top of each other (basic-box, luminaire,
              halftone) plus the tab-driven featured preview in the
              foreground. On mobile the right column is hidden so
              the dark hero collapses to a clean two-line block.
            The dark hero soft-fades into the existing light catalog
            below via the .hero-dark-edge gradient overlay.
        */}
        <section className="hero-dark text-zinc-100 relative overflow-hidden">
          <div className="hero-dark-edge absolute inset-0 pointer-events-none" />
          <div className="max-w-[1600px] mx-auto px-3 sm:px-5 pt-4 sm:pt-6 pb-4 sm:pb-6 grid lg:grid-cols-[1.1fr_0.9fr] gap-4 sm:gap-6 items-center relative">
            {/* LEFT — copy + CTAs + category tabs */}
            <div className="min-w-0">
              {/* Tag row above the headline — small uppercase eyebrow
                  reminiscent of premium SaaS landing pages. */}
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-[10px] sm:text-[11px] font-chunky uppercase tracking-[0.18em] mb-2 sm:mb-3">
                <Sparkles className="w-3 h-3" />
                {t('heroPerk2')}
              </div>
              <h1 className="font-chunky text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white leading-[1.05] tracking-tight break-words">
                {t('heroTitle')}
              </h1>
              <p className="mt-2 sm:mt-3 text-zinc-400 text-sm sm:text-base lg:text-lg max-w-2xl leading-snug break-words">
                {t('heroSub')}
              </p>

              {/* CTA row */}
              <div className="mt-3 sm:mt-4 flex flex-wrap items-center gap-3">
                <a
                  href="#templates"
                  onClick={scrollTo('templates')}
                  className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 active:bg-cyan-600 text-zinc-950 font-chunky uppercase tracking-wider text-xs sm:text-sm px-5 py-2.5 sm:py-3 rounded-md shadow-[0_8px_24px_-8px_rgba(34,211,238,0.6)] hover:shadow-[0_12px_36px_-8px_rgba(34,211,238,0.8)] transition-all hover:-translate-y-0.5"
                >
                  <Sparkles className="w-4 h-4" />
                  {t('heroCta')}
                  <ArrowRight className="w-4 h-4" />
                </a>
                <a
                  href="#how-it-works"
                  onClick={scrollTo('how-it-works')}
                  className="inline-flex items-center gap-2 px-4 py-2.5 sm:py-3 rounded-md text-xs sm:text-sm font-chunky uppercase tracking-wider text-zinc-200 border border-zinc-700 hover:border-cyan-400/50 hover:text-cyan-300 hover:bg-zinc-900/50 transition-all"
                >
                  {t('heroCtaSub')}
                </a>
              </div>

              {/* Reassurance perks — tighter, lighter, dark-mode optimised. */}
              <div className="mt-2.5 sm:mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-zinc-400 text-xs sm:text-[13px]">
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />{t('heroPerk1')}</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />{t('heroPerk2')}</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />{t('heroPerk3')}</span>
              </div>

              {/* Category tabs — colored chips that swap the hero preview.
                  Clicking also smooth-scrolls down to the corresponding
                  category section so the tab acts as a shortcut. The
                  active tab is filled with the category accent colour. */}
              <div className="mt-3 sm:mt-4 flex flex-wrap items-center gap-2">
                {CATEGORIES.filter((c) => bucket(c.id).length > 0).map(({ id, icon: Icon, accent }) => {
                  const active = heroTab === id;
                  // Per-accent class lookup. Tailwind's JIT requires the
                  // full class name to appear in source — so we keep an
                  // explicit map instead of building strings dynamically.
                  const accentMap = {
                    cyan:    { active: 'bg-cyan-500 border-cyan-400 text-zinc-950',       idle: 'border-zinc-700 text-zinc-300 hover:border-cyan-400/60 hover:text-cyan-300' },
                    amber:   { active: 'bg-amber-400 border-amber-300 text-zinc-950',     idle: 'border-zinc-700 text-zinc-300 hover:border-amber-400/60 hover:text-amber-200' },
                    fuchsia: { active: 'bg-fuchsia-500 border-fuchsia-400 text-white',    idle: 'border-zinc-700 text-zinc-300 hover:border-fuchsia-400/60 hover:text-fuchsia-300' },
                    emerald: { active: 'bg-emerald-500 border-emerald-400 text-zinc-950', idle: 'border-zinc-700 text-zinc-300 hover:border-emerald-400/60 hover:text-emerald-300' },
                    violet:  { active: 'bg-violet-500 border-violet-400 text-white',      idle: 'border-zinc-700 text-zinc-300 hover:border-violet-400/60 hover:text-violet-300' },
                    sky:     { active: 'bg-sky-500 border-sky-400 text-white',            idle: 'border-zinc-700 text-zinc-300 hover:border-sky-400/60 hover:text-sky-300' },
                    orange:  { active: 'bg-orange-500 border-orange-400 text-zinc-950',   idle: 'border-zinc-700 text-zinc-300 hover:border-orange-400/60 hover:text-orange-300' },
                    rose:    { active: 'bg-rose-500 border-rose-400 text-white',          idle: 'border-zinc-700 text-zinc-300 hover:border-rose-400/60 hover:text-rose-300' },
                    lime:    { active: 'bg-lime-500 border-lime-400 text-zinc-950',       idle: 'border-zinc-700 text-zinc-300 hover:border-lime-400/60 hover:text-lime-300' },
                    teal:    { active: 'bg-teal-500 border-teal-400 text-white',          idle: 'border-zinc-700 text-zinc-300 hover:border-teal-400/60 hover:text-teal-300' },
                    indigo:  { active: 'bg-indigo-500 border-indigo-400 text-white',      idle: 'border-zinc-700 text-zinc-300 hover:border-indigo-400/60 hover:text-indigo-300' },
                    pink:    { active: 'bg-pink-500 border-pink-400 text-white',          idle: 'border-zinc-700 text-zinc-300 hover:border-pink-400/60 hover:text-pink-300' },
                  };
                  const cls = active ? (accentMap[accent] || accentMap.cyan).active : (accentMap[accent] || accentMap.cyan).idle;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setHeroTab(id)}
                      className={`cat-tab inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] sm:text-xs font-chunky uppercase tracking-wider ${cls}`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {catTitle(id)}
                      <span className={`text-[10px] font-mono ${active ? 'opacity-80' : 'opacity-60'}`}>{bucket(id).length}</span>
                    </button>
                  );
                })}
              </div>

              {/* 3-step chips kept (compact) — they explain the workflow. */}
              <div id="how-it-works" className="mt-3 flex flex-wrap items-center gap-x-1.5 gap-y-1.5">
                {[
                  { n: 1, icon: MousePointerClick, title: t('howStep1Title') },
                  { n: 2, icon: Sliders,           title: t('howStep2Title') },
                  { n: 3, icon: Download,          title: t('howStep3Title') },
                ].map(({ n, icon: Icon, title }, i) => (
                  <span key={n} className="inline-flex items-center gap-1.5">
                    {i > 0 && <span className="text-zinc-600 text-[13px]">›</span>}
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-zinc-900/60 border border-zinc-800 text-zinc-300 font-chunky uppercase tracking-wider text-[10px] sm:text-[11px]">
                      <span className="w-4 h-4 rounded-full bg-cyan-500 text-zinc-950 grid place-items-center text-[9px]">{n}</span>
                      <Icon className="w-3.5 h-3.5" />
                      {title}
                    </span>
                  </span>
                ))}
              </div>
            </div>

            {/* RIGHT — tilted preview composition. Hidden on phones to
                keep the dark hero short above the fold. The featured
                preview (driven by `heroTab`) sits in the foreground;
                two secondary previews hover behind it at slight angles
                so the whole composition reads like a "deck of work". */}
            <div className="hidden lg:block relative h-[320px] xl:h-[360px]">
              {/* Decorative blueprint frame */}
              <div className="absolute inset-0 rounded-2xl border border-zinc-800/80 bg-gradient-to-br from-zinc-900/60 via-zinc-950 to-zinc-900/40 overflow-hidden">
                <div className="absolute inset-0 opacity-30 blueprint-grid" />
              </div>
              {/* Back card #1 — leftmost, tilted left */}
              <div className="absolute left-6 top-6 w-48 h-36 rounded-xl bg-white border border-zinc-200 overflow-hidden shadow-2xl shadow-black/40 -rotate-6 hero-stack-img">
                <img src="/preview/luminaire.webp" alt="" className="w-full h-full object-contain p-3" />
                <div className="absolute bottom-1.5 left-2 text-[9px] font-chunky uppercase tracking-[0.18em] text-zinc-500">{t('catLuminaires')}</div>
              </div>
              {/* Back card #2 — rightmost, tilted right */}
              <div className="absolute right-4 top-3 w-52 h-36 rounded-xl bg-white border border-zinc-200 overflow-hidden shadow-2xl shadow-black/40 rotate-6 hero-stack-img">
                <img src="/preview/halftone.webp" alt="" className="w-full h-full object-contain p-3" />
                <div className="absolute bottom-1.5 left-2 text-[9px] font-chunky uppercase tracking-[0.18em] text-zinc-500">{t('catPuzzles')}</div>
              </div>
              {/* Foreground card — driven by selected tab */}
              <div key={heroTab} className="absolute left-1/2 -translate-x-1/2 bottom-4 w-64 h-48 rounded-xl bg-white border border-zinc-200 overflow-hidden shadow-[0_30px_60px_-20px_rgba(0,0,0,0.7)] hero-stack-img">
                <img
                  src={CATEGORIES.find((c) => c.id === heroTab)?.preview || '/preview/basic-box.webp'}
                  alt=""
                  className="w-full h-full object-contain p-4"
                />
                {/* Category accent strip on top — colour comes from the
                    active category's accent token (dynamic). */}
                <div className={`absolute top-0 inset-x-0 h-1 ${({
                  cyan: 'bg-cyan-500', amber: 'bg-amber-400', fuchsia: 'bg-fuchsia-500',
                  emerald: 'bg-emerald-500', violet: 'bg-violet-500', sky: 'bg-sky-500',
                  orange: 'bg-orange-500', rose: 'bg-rose-500', lime: 'bg-lime-500',
                  teal: 'bg-teal-500', indigo: 'bg-indigo-500', pink: 'bg-pink-500',
                }[CATEGORIES.find((c) => c.id === heroTab)?.accent] || 'bg-sky-500')}`} />
                <div className="absolute bottom-2 left-3 text-[10px] font-chunky uppercase tracking-[0.2em] text-zinc-700 bg-white/90 px-1.5 py-0.5 rounded">
                  {catTitle(heroTab)}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── HOW IT WORKS — Sprint 3 ───────────────────────────────────
            Premium 3-step block with a kicker, big title and 3 large
            cards. Each card has an oversized step number (display
            type), the step icon, a title, a one-line teaser and a
            longer detail paragraph. On lg+ the cards are connected by
            a horizontal dotted line that visually chains the steps. */}
        <section className="bg-white border-y border-zinc-200">
          <div className="max-w-[1600px] mx-auto px-3 sm:px-5 py-3 sm:py-4">
            {/* Compact heading row — kicker pill + title sit on the
                same line on md+ so the block keeps its hierarchy
                without burning vertical space. Sub copy goes inline
                on the right (md+) or wraps below on phones. */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2.5 sm:mb-3">
              <div className="flex items-center gap-3 min-w-0">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-cyan-50 border border-cyan-200 text-cyan-700 text-[10px] font-chunky uppercase tracking-[0.18em] shrink-0">
                  <Sparkles className="w-3 h-3" />
                  {t('howItWorksKicker')}
                </span>
                <h2 className="font-chunky text-lg sm:text-xl md:text-2xl text-zinc-950 leading-tight truncate">{t('howItWorks')}</h2>
              </div>
              <p className="text-zinc-600 text-xs sm:text-sm leading-snug md:max-w-md md:text-right">{t('howItWorksSub')}</p>
            </div>
            <div className="relative grid grid-cols-1 md:grid-cols-3 gap-2 sm:gap-3">
              {/* Connecting dotted line (md+) — recalibrated to the
                  smaller card padding so it lines up with the centre
                  of each step circle. */}
              <div className="hidden md:block absolute top-7 left-[16.6%] right-[16.6%] h-px border-t-2 border-dashed border-zinc-200" />
              {[
                { n: 1, Icon: MousePointerClick, title: t('howStep1Title'), teaser: t('howStep1Desc'), detail: t('howStep1Detail'), accent: 'cyan' },
                { n: 2, Icon: Sliders,           title: t('howStep2Title'), teaser: t('howStep2Desc'), detail: t('howStep2Detail'), accent: 'fuchsia' },
                { n: 3, Icon: Download,          title: t('howStep3Title'), teaser: t('howStep3Desc'), detail: t('howStep3Detail'), accent: 'emerald' },
              ].map(({ n, Icon, title, teaser, detail, accent }) => {
                const accentMap = {
                  cyan:    { bg: 'bg-cyan-500', ring: 'ring-cyan-200', text: 'text-cyan-700', soft: 'bg-cyan-50 border-cyan-100' },
                  fuchsia: { bg: 'bg-fuchsia-500', ring: 'ring-fuchsia-200', text: 'text-fuchsia-700', soft: 'bg-fuchsia-50 border-fuchsia-100' },
                  emerald: { bg: 'bg-emerald-500', ring: 'ring-emerald-200', text: 'text-emerald-700', soft: 'bg-emerald-50 border-emerald-100' },
                };
                const a = accentMap[accent];
                return (
                  <div key={n} className="relative bg-white rounded-xl border border-zinc-200 p-3 sm:p-3.5 hover:border-zinc-300 hover:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.12)] transition-all card-lift">
                    {/* Header row — small step circle, teaser pill on
                        the right. Keeps the whole step on a single
                        horizontal band for a tight vertical rhythm. */}
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className={`relative z-10 grid place-items-center w-7 h-7 rounded-full ${a.bg} text-white shadow ring-2 ring-white ${a.ring} shrink-0`}>
                        <span className="font-chunky text-xs">{n}</span>
                      </div>
                      <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border ${a.soft} ${a.text} font-chunky uppercase tracking-wider text-[9px] truncate`}>
                        <Icon className="w-3 h-3 shrink-0" />
                        <span className="truncate">{teaser}</span>
                      </div>
                    </div>
                    <h3 className="font-chunky text-sm sm:text-base text-zinc-950 leading-tight">{title}</h3>
                    <p className="mt-1 text-zinc-600 text-[12px] sm:text-[12.5px] leading-snug line-clamp-2">{detail}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── CATEGORIZED TEMPLATES ─────────────────────────────────────
            Each category becomes its own <section> with a header
            (icon + title + short description + count) and a 4-column
            grid of cards. The grid is identical across categories so
            the visual rhythm stays consistent — only the section
            heading changes. */}
        <div id="templates" className="max-w-[1600px] mx-auto px-3 sm:px-5 pt-4 pb-8 scroll-mt-16">
          {CATEGORIES.filter((c) => bucket(c.id).length > 0).map(({ id, icon: Icon, accent }) => {
            const chipCls = ({
              cyan: 'bg-cyan-50 border-cyan-100 text-cyan-700',
              amber: 'bg-amber-50 border-amber-100 text-amber-700',
              fuchsia: 'bg-fuchsia-50 border-fuchsia-100 text-fuchsia-700',
              emerald: 'bg-emerald-50 border-emerald-100 text-emerald-700',
              violet: 'bg-violet-50 border-violet-100 text-violet-700',
              sky: 'bg-sky-50 border-sky-100 text-sky-700',
              orange: 'bg-orange-50 border-orange-100 text-orange-700',
              rose: 'bg-rose-50 border-rose-100 text-rose-700',
              lime: 'bg-lime-50 border-lime-100 text-lime-700',
              teal: 'bg-teal-50 border-teal-100 text-teal-700',
              indigo: 'bg-indigo-50 border-indigo-100 text-indigo-700',
              pink: 'bg-pink-50 border-pink-100 text-pink-700',
            }[accent] || 'bg-cyan-50 border-cyan-100 text-cyan-700');
            const desc = catDesc(id);
            return (
            <section key={id} id={`cat-${id}`} className="scroll-mt-16 mb-8 sm:mb-10">
              <header className="mb-3 sm:mb-4 flex items-end justify-between gap-3 border-b border-zinc-200 pb-2">
                <div className="flex items-center gap-2.5">
                  <span className={`grid place-items-center w-8 h-8 sm:w-9 sm:h-9 rounded-lg border ${chipCls}`}>
                    <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                  </span>
                  <div>
                    <h2 className="font-chunky text-base sm:text-lg text-zinc-950 leading-tight">{catTitle(id)}</h2>
                    {desc && <p className="text-zinc-500 text-[11px] sm:text-xs leading-tight">{desc}</p>}
                  </div>
                </div>
                <span className="text-[10px] sm:text-xs font-mono text-zinc-400 shrink-0">{bucket(id).length}</span>
              </header>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                {bucket(id).map((box) => (
                  <BoxCard
                    key={box.id}
                    box={box}
                    lang={lang}
                    ctaLabel={t('cardCta')}
                    ctaSoonLabel={t('cardCtaSoon')}
                    ctaDisabledLabel={t('cardCtaDisabled')}
                    featuredLabel={t('featuredLabel')}
                    categoryAccent={accent}
                    CategoryIcon={Icon}
                  />
                ))}
              </div>
            </section>
            );
          })}

          {/* "Em desenvolvimento" — collapsed by default. Uses native
              <details>/<summary> for zero-JS accessibility. The cards
              inside are still BoxCards (so they show the same visual
              language) but they're already disabled by their flag. */}
          {grouped.comingSoon.length > 0 && (
            <section id="cat-coming-soon" className="scroll-mt-14 mb-8">
              <details className="group rounded-xl border border-amber-200 bg-amber-50/50 overflow-hidden">
                <summary className="cursor-pointer list-none px-4 py-3 flex items-center justify-between gap-3 hover:bg-amber-100/50 transition-colors">
                  <div className="flex items-center gap-2.5">
                    <span className="text-lg">🚧</span>
                    <div>
                      <h2 className="font-chunky text-sm sm:text-base text-amber-900 leading-tight">{t('catComingSoon')}</h2>
                      <p className="text-amber-700/80 text-[11px] sm:text-xs leading-tight">{t('catComingSoonExpand')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] sm:text-xs font-mono text-amber-700">{grouped.comingSoon.length}</span>
                    <ChevronDown className="w-4 h-4 text-amber-700 group-open:rotate-180 transition-transform" />
                  </div>
                </summary>
                <div className="px-3 sm:px-4 pb-4 pt-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                    {grouped.comingSoon.map((box) => {
                      // For coming-soon cards we still resolve the
                      // accent from the original category so the card
                      // visually matches its peers when promoted.
                      const cat = CATEGORIES.find((c) => c.id === getToolCategory(box.slug));
                      return (
                        <BoxCard
                          key={box.id}
                          box={box}
                          lang={lang}
                          ctaLabel={t('cardCta')}
                          ctaSoonLabel={t('cardCtaSoon')}
                          ctaDisabledLabel={t('cardCtaDisabled')}
                          featuredLabel={t('featuredLabel')}
                          categoryAccent={cat?.accent || 'amber'}
                          CategoryIcon={cat?.icon || Wrench}
                        />
                      );
                    })}
                  </div>
                </div>
              </details>
            </section>
          )}
        </div>

        {/* ── REAL PROJECTS GALLERY — Sprint 4 ───────────────────────────
            Six-tile mosaic that showcases real cuts using the existing
            preview assets as stand-ins (until photos are provided). The
            mosaic mixes 1×1 and 2×1 tiles so the visual reads as a
            curated showcase, not a uniform grid. Each tile carries a
            soft caption pinned to the bottom-left. The "projects" id
            is the anchor target for the Header "Exemplos" nav link. */}
        <section id="projects" className="bg-zinc-50 border-y border-zinc-200 scroll-mt-16">
          <div className="max-w-[1600px] mx-auto px-3 sm:px-5 py-10 sm:py-14">
            <div className="text-center max-w-3xl mx-auto mb-6 sm:mb-8">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-fuchsia-50 border border-fuchsia-200 text-fuchsia-700 text-[10px] sm:text-[11px] font-chunky uppercase tracking-[0.18em]">
                <Layers className="w-3 h-3" />
                {t('projectsKicker')}
              </span>
              <h2 className="font-chunky text-2xl sm:text-3xl md:text-4xl text-zinc-950 mt-3 leading-tight">{t('projectsTitle')}</h2>
              <p className="mt-2 text-zinc-600 text-sm sm:text-base leading-relaxed">{t('projectsSub')}</p>
            </div>
            {/* 6-tile mosaic — uses asymmetric column spans for a curated
                "magazine spread" look. Hover lifts each tile slightly. */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
              {[
                { src: '/preview/basic-box.webp',     label: t('catBoxes'),       span: 'md:col-span-2 md:row-span-2 aspect-square md:aspect-auto' },
                { src: '/preview/luminaire.webp',     label: t('catLuminaires'),  span: 'aspect-square' },
                { src: '/preview/halftone.webp',      label: t('catPuzzles'),     span: 'aspect-square' },
                { src: '/preview/luminaire-shapes.webp', label: t('catLuminaires'), span: 'aspect-square' },
                { src: '/preview/grid-divider-box.webp', label: t('catDecoratives'), span: 'aspect-square' },
                { src: '/preview/cardboard-box.webp', label: t('catBoxes'),       span: 'md:col-span-2 aspect-square md:aspect-[2/1]' },
                { src: '/preview/ballot-box.webp',    label: t('catBoxes'),       span: 'aspect-square' },
              ].map((tile, i) => (
                <div key={i} className={`group relative rounded-xl overflow-hidden bg-white border border-zinc-200 hover:border-zinc-300 hover:shadow-[0_18px_50px_-18px_rgba(0,0,0,0.25)] transition-all card-lift ${tile.span}`}>
                  <div className="absolute inset-0 blueprint-grid opacity-50" />
                  <img src={tile.src} alt="" className="absolute inset-0 w-full h-full object-contain p-3 sm:p-4 transition-transform duration-500 group-hover:scale-[1.04]" />
                  <div className="absolute inset-x-0 bottom-0 px-3 py-2 bg-gradient-to-t from-zinc-950/70 via-zinc-950/30 to-transparent">
                    <span className="text-[9px] sm:text-[10px] font-chunky uppercase tracking-[0.18em] text-white">{tile.label}</span>
                  </div>
                </div>
              ))}
            </div>
            {/* CTA into the community gallery — direct people who want
                more from the curated mosaic into the user-submitted
                /examples page. Logged-in users will see a "submit"
                button on that page; anonymous users can browse first. */}
            <div className="mt-6 sm:mt-8 text-center">
              <Link
                to="/examples"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-white border border-fuchsia-300 hover:border-fuchsia-500 hover:bg-fuchsia-50 text-fuchsia-700 font-chunky uppercase tracking-wider text-xs transition-colors"
              >
                {t('navExamples')} →
              </Link>
            </div>
          </div>
        </section>

        {/* ── WHY MAKERCUTT — Sprint 4 ───────────────────────────────────
            5 perks in a 5-column grid (collapses to 2/1 on smaller
            screens). Each perk: round colored icon chip + chunky title
            + 2-line description. Designed to read like a "value props"
            band right before the final CTA banner. */}
        <section className="bg-white">
          <div className="max-w-[1600px] mx-auto px-3 sm:px-5 py-10 sm:py-14">
            <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-10">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] sm:text-[11px] font-chunky uppercase tracking-[0.18em]">
                <CheckCircle2 className="w-3 h-3" />
                {t('whyKicker')}
              </span>
              <h2 className="font-chunky text-2xl sm:text-3xl md:text-4xl text-zinc-950 mt-3 leading-tight">{t('whyTitle')}</h2>
              <p className="mt-2 text-zinc-600 text-sm sm:text-base leading-relaxed">{t('whySub')}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-5">
              {[
                { Icon: Globe,    title: t('whyPerk1Title'), desc: t('whyPerk1Desc'), accent: 'cyan' },
                { Icon: Layers,   title: t('whyPerk2Title'), desc: t('whyPerk2Desc'), accent: 'fuchsia' },
                { Icon: Eye,      title: t('whyPerk3Title'), desc: t('whyPerk3Desc'), accent: 'amber' },
                { Icon: Cog,      title: t('whyPerk4Title'), desc: t('whyPerk4Desc'), accent: 'emerald' },
                { Icon: Heart,    title: t('whyPerk5Title'), desc: t('whyPerk5Desc'), accent: 'rose' },
              ].map(({ Icon, title, desc, accent }, i) => {
                const accentMap = {
                  cyan:    'bg-cyan-100 text-cyan-700',
                  fuchsia: 'bg-fuchsia-100 text-fuchsia-700',
                  amber:   'bg-amber-100 text-amber-700',
                  emerald: 'bg-emerald-100 text-emerald-700',
                  rose:    'bg-rose-100 text-rose-700',
                };
                return (
                  <div key={i} className="text-center sm:text-left">
                    <div className={`inline-grid place-items-center w-11 h-11 rounded-full ${accentMap[accent]} mb-3`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className="font-chunky text-base text-zinc-950 leading-tight">{title}</h3>
                    <p className="mt-1.5 text-zinc-600 text-[13px] leading-relaxed">{desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── FINAL CTA BANNER — Sprint 5 ────────────────────────────────
            Premium dark banner mirroring the hero palette so the page
            "loops back" visually. Big chunky title + sub copy + single
            outsized CTA button with a glow shadow. */}
        <section className="hero-dark text-zinc-100 relative overflow-hidden">
          <div className="max-w-[1600px] mx-auto px-3 sm:px-5 py-12 sm:py-16 relative text-center">
            <h2 className="font-chunky text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white leading-[1.05] tracking-tight">
              {t('ctaBannerTitle')}
            </h2>
            <p className="mt-3 text-zinc-300 text-sm sm:text-base lg:text-lg max-w-2xl mx-auto leading-relaxed">
              {t('ctaBannerSub')}
            </p>
            <div className="mt-6 sm:mt-7 flex justify-center">
              <a
                href="#templates"
                onClick={scrollTo('templates')}
                className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 active:bg-cyan-600 text-zinc-950 font-chunky uppercase tracking-wider text-sm sm:text-base px-6 sm:px-7 py-3 sm:py-3.5 rounded-md shadow-[0_12px_36px_-8px_rgba(34,211,238,0.7)] hover:shadow-[0_16px_50px_-8px_rgba(34,211,238,0.9)] transition-all hover:-translate-y-0.5"
              >
                <Sparkles className="w-4 h-4" />
                {t('ctaBannerBtn')}
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>

        {/* ── Consolidated FAQ ──────────────────────────────────────
            Single FAQ for the whole site — previously the site had FAQs
            scattered across tool pages, /pricing, /influencer and /vs.
            They were all removed and unified into this block so users have
            one canonical place to find answers, and Google sees a single
            FAQPage payload tied to the highest-authority URL. */}
        <HomeFaq />
      </main>
      <Footer />
    </div>
  );
}

// Per-category accent map used by the redesigned card. Tailwind JIT
// requires literal class names, so we enumerate every variant here.
// `bar` paints the top accent strip; `iconBg`/`iconText` colour the
// small icon chip in the card header; `cta` styles the bottom CTA pill
// in idle / hover states; `ring` is the optional subtle outline.
const CARD_ACCENTS = {
  cyan:    { bar: 'bg-cyan-500',    iconBg: 'bg-cyan-50 border-cyan-200',       iconText: 'text-cyan-700',    cta: 'bg-cyan-50 text-cyan-700 border-cyan-200 group-hover:bg-cyan-600 group-hover:text-white group-hover:border-cyan-600',                ring: 'hover:border-cyan-500',    glow: 'hover:shadow-[0_12px_40px_-12px_rgba(6,182,212,0.35)]' },
  amber:   { bar: 'bg-amber-400',   iconBg: 'bg-amber-50 border-amber-200',     iconText: 'text-amber-700',   cta: 'bg-amber-50 text-amber-800 border-amber-200 group-hover:bg-amber-500 group-hover:text-zinc-950 group-hover:border-amber-500',           ring: 'hover:border-amber-400',   glow: 'hover:shadow-[0_12px_40px_-12px_rgba(251,191,36,0.35)]' },
  fuchsia: { bar: 'bg-fuchsia-500', iconBg: 'bg-fuchsia-50 border-fuchsia-200', iconText: 'text-fuchsia-700', cta: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200 group-hover:bg-fuchsia-600 group-hover:text-white group-hover:border-fuchsia-600',  ring: 'hover:border-fuchsia-500', glow: 'hover:shadow-[0_12px_40px_-12px_rgba(217,70,239,0.35)]' },
  emerald: { bar: 'bg-emerald-500', iconBg: 'bg-emerald-50 border-emerald-200', iconText: 'text-emerald-700', cta: 'bg-emerald-50 text-emerald-700 border-emerald-200 group-hover:bg-emerald-600 group-hover:text-white group-hover:border-emerald-600',  ring: 'hover:border-emerald-500', glow: 'hover:shadow-[0_12px_40px_-12px_rgba(16,185,129,0.35)]' },
  violet:  { bar: 'bg-violet-500',  iconBg: 'bg-violet-50 border-violet-200',   iconText: 'text-violet-700',  cta: 'bg-violet-50 text-violet-700 border-violet-200 group-hover:bg-violet-600 group-hover:text-white group-hover:border-violet-600',       ring: 'hover:border-violet-500',  glow: 'hover:shadow-[0_12px_40px_-12px_rgba(139,92,246,0.35)]' },
  sky:     { bar: 'bg-sky-500',     iconBg: 'bg-sky-50 border-sky-200',         iconText: 'text-sky-700',     cta: 'bg-sky-50 text-sky-700 border-sky-200 group-hover:bg-sky-600 group-hover:text-white group-hover:border-sky-600',                      ring: 'hover:border-sky-500',     glow: 'hover:shadow-[0_12px_40px_-12px_rgba(14,165,233,0.35)]' },
  orange:  { bar: 'bg-orange-500',  iconBg: 'bg-orange-50 border-orange-200',   iconText: 'text-orange-700',  cta: 'bg-orange-50 text-orange-700 border-orange-200 group-hover:bg-orange-600 group-hover:text-white group-hover:border-orange-600',          ring: 'hover:border-orange-500',  glow: 'hover:shadow-[0_12px_40px_-12px_rgba(249,115,22,0.35)]' },
};

function BoxCard({ box, lang, ctaLabel, ctaSoonLabel, ctaDisabledLabel, featuredLabel, categoryAccent = 'cyan', CategoryIcon = Package }) {
  // Tier (and therefore the visual badge) is derived from the catalogue
  // helper so admin overrides at runtime stay in sync with the gating.
  // Tier vocabulary (post 4-tier rollout): free | maker | pro | team | draft
  // The cascade rule (free⊂maker⊂pro⊂team) is documented inline below
  // and rendered as a tiny "unlocked by" strip under the CTA so users
  // immediately understand which plans grant access.
  const tier = getToolTier(box.slug);
  const isPaid = tier === 'maker' || tier === 'pro' || tier === 'team';
  const isPro = tier === 'pro';
  const isMakerTool = tier === 'maker';
  const isTeamTool = tier === 'team';
  const isDraftTool = tier === 'draft';
  // "Mais usada" comes from the override-aware helper so admins can
  // promote/demote any tool from /admin without a redeploy. Only ONE
  // tool can be featured at a time (backend enforces this on PATCH).
  const featured = isToolFeatured(box.slug);
  // Admin-only: a tool may be hidden via "enabled = false" but we still
  // render it for admins so the card shows up visibly disabled (so
  // they can flip it back on from this very screen mentally / from
  // /admin). visibleBoxTypes() already filtered it out for everyone else.
  const enabled = isToolEnabled(box.slug);
  const tag = getToolTag(box.slug, lang);
  // Extra badge ('new' | 'exclusive' | 'none') driven by the admin
  // panel — ribbon at the top-left of the card to maximise visibility
  // without colliding with the Free/Pro/Draft pill at the top-right.
  const extraBadge = getToolBadge(box.slug);
  const extraLabel = getToolBadgeLabel(box.slug, lang);
  // "Em breve" is a special preview-only state: the card is visible
  // but the user CANNOT click it (no route, no Configurator). We
  // detect it from either the badge ('soon') or the legacy catalogue
  // flag (`disabled: true`).
  const comingSoon = isToolComingSoon(box.slug);
  // Tier-specific styling for the accent bar (top edge of the card)
  // and the Free/Maker/Pro/Team pill at the top-right. Each plan owns
  // a distinct colour token so users learn the visual association:
  //   free   → cyan    (default brand)
  //   maker  → emerald (entry paid plan)
  //   pro    → fuchsia (popular plan, keeps existing branding)
  //   team   → indigo  (multi-seat / business)
  //   draft  → amber   (admin only)
  const accentBar = isDraftTool ? 'bg-amber-500'
    : isTeamTool   ? 'bg-indigo-500'
    : isPro        ? 'bg-fuchsia-500'
    : isMakerTool  ? 'bg-emerald-500'
    : 'bg-cyan-500';
  const badgeStyle = isDraftTool ? 'bg-amber-500 text-zinc-950'
    : isTeamTool   ? 'bg-indigo-500 text-white'
    : isPro        ? 'bg-fuchsia-500 text-white'
    : isMakerTool  ? 'bg-emerald-500 text-white'
    : 'bg-zinc-900 text-cyan-300';
  // Diagonal ribbon variant for the extra badge. Each variant is
  // implemented as a CSS class in App.css (.ribbon--new / --launch /
  // --exclusive / --soon) — see that file for the visual design.
  // Ribbon sits on the TOP-LEFT corner of the card, rotated -45°, so
  // it reads like a wrap-around silk band (similar to the "Best
  // Seller" tags on e-commerce thumbnails).
  const ribbonVariant =
    extraBadge === 'new' ? 'ribbon--new'
      : extraBadge === 'launch' ? 'ribbon--launch'
      : extraBadge === 'soon' ? 'ribbon--soon'
      : extraBadge === 'exclusive' ? 'ribbon--exclusive'
      : null;
  // Dimmed visual when the card is not actionable (admin-disabled or
  // coming-soon). For coming-soon we keep more contrast (0.85) than
  // disabled (0.65) so the preview still feels exciting.
  const dimmed = !enabled || box.disabled;
  const softened = comingSoon && enabled && !box.disabled;
  // Resolve the per-category accent (Sprint 2). The card now reflects
  // the colour token of its parent category — accent bar, icon chip
  // and CTA all share one token so the user gets a consistent visual
  // cue per "type of tool". Tier (free/pro/draft) keeps owning the
  // top-right tag instead.
  const acc = CARD_ACCENTS[categoryAccent] || CARD_ACCENTS.cyan;
  const content = (
    <article className={`group relative rounded-xl overflow-hidden bg-white border transition-all duration-300 ${
      featured && !dimmed && !softened
        ? `border-cyan-400 ring-1 ring-cyan-300/40 shadow-[0_4px_20px_-8px_rgba(6,182,212,0.35)] hover:border-cyan-600 hover:-translate-y-0.5 hover:shadow-[0_12px_40px_-12px_rgba(6,182,212,0.45)] card-lift`
        : `border-zinc-200 ${acc.ring}`
    } ${
      dimmed
        ? 'opacity-65'
        : softened
          ? 'opacity-90'
          : !featured && `hover:-translate-y-0.5 ${acc.glow} card-lift`
    }`}>
      {/* Category accent bar (top edge) — stays full-width and slightly
          thicker than before so the colour reads instantly. */}
      <div className={`absolute top-0 inset-x-0 h-1 ${acc.bar} opacity-80 group-hover:opacity-100`} />
      {/* Admin-state pills ("admin only" / "disabled"). When the corner
          ribbon is also present we shift these pills to the top-right
          row (just under the Free/Pro/Draft tag) so they don't overlap
          the diagonal band. */}
      {isDraftTool && (
        <div className={`absolute ${ribbonVariant ? 'top-12 right-3' : 'top-3 left-3'} text-[9px] font-mono uppercase tracking-[0.2em] px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 z-20`}>
          {lang === 'en' ? 'admin only' : 'somente admin'}
        </div>
      )}
      {!enabled && !isDraftTool && (
        <div className={`absolute ${ribbonVariant ? 'top-12 right-3' : 'top-3 left-3'} text-[9px] font-mono uppercase tracking-[0.2em] px-2 py-0.5 rounded bg-zinc-200 text-zinc-700 border border-zinc-300 z-20`}>
          {lang === 'en' ? 'disabled' : 'desativada'}
        </div>
      )}
      {/* Extra badge ('Novo' / 'Lançamento' / 'Exclusivo' / 'Em breve').
          Rendered as a diagonal corner ribbon (see App.css). Hidden when
          variant is 'none'. The ribbon visually wraps the top-left
          corner of the card so it reads as a "silk band" rather than a
          flat pill. */}
      {ribbonVariant && extraLabel && (
        <div className="ribbon-wrap">
          <span className={`ribbon ${ribbonVariant}`}>{extraLabel}</span>
        </div>
      )}
      {/* Preview area — the photo IS the message. Taller frame
          (h-52 = 208px / h-56 = 224px) with ZERO internal padding so
          the artwork uses 100% of the frame. The badge floats over the
          top-right corner (absolute, z-20) without reserving vertical
          space, and `object-contain` on the inner <img> guarantees the
          FULL image stays visible. A subtle bottom border separates
          the photo from the description so it never looks "clipped". */}
      <div className="relative h-52 sm:h-56 bg-zinc-50 overflow-hidden border-b border-zinc-100">
        <div className="absolute inset-0 blueprint-grid opacity-60" />
        {/* "Mais usada" selo — top-LEFT, only when box.featured is set
            (currently the Caixa Básica). Cyan ribbon with a star icon
            so the user immediately spots the recommended starting tool.
            Coexists with the Free/Pro tag on the top-right. */}
        {featured && !ribbonVariant && (
          <div className="absolute top-2 left-2 z-20 inline-flex items-center gap-1 text-[9px] font-chunky uppercase tracking-[0.18em] px-2 py-0.5 rounded shadow-sm bg-cyan-600 text-white">
            <Star className="w-3 h-3 fill-current" />
            {featuredLabel}
          </div>
        )}
        <div className={`absolute top-2 right-2 z-20 text-[9px] font-chunky uppercase tracking-[0.18em] px-2 py-0.5 rounded shadow-sm ${badgeStyle}`}>{tag}</div>
        <div className="absolute inset-0">
          <BoxPreviewCard type={box.id} />
        </div>
      </div>
      {/* Card body — mock-up inspired: small colored icon chip on the
          left (category icon), title + description column on the right,
          CTA pill below taking the full width. The icon chip uses the
          same accent token as the top bar, giving every card an
          instant "type marker" the user can scan visually. */}
      <div className="px-3.5 py-3 flex flex-col">
        <div className="flex items-start gap-2.5 mb-1">
          <span className={`shrink-0 grid place-items-center w-8 h-8 rounded-lg border ${acc.iconBg} ${acc.iconText}`}>
            <CategoryIcon className="w-4 h-4" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="font-chunky text-base text-zinc-950 leading-tight flex items-center justify-between gap-2">
              <span className="truncate">{box.name[lang]}</span>
              {!box.disabled && !comingSoon && <ArrowRight className="w-3.5 h-3.5 text-zinc-400 group-hover:text-zinc-700 group-hover:translate-x-0.5 transition-all shrink-0" />}
            </h3>
            {/* Friendly, plain-language description (no jargon) so the
                user understands the tool in 1-2 seconds without reading
                long copy. Clamped to 2 lines for identical card heights. */}
            {box.desc && box.desc[lang] && (
              <p className="text-zinc-600 text-[12.5px] leading-snug mt-0.5 line-clamp-2 min-h-[32px]">{box.desc[lang]}</p>
            )}
          </div>
        </div>
        {/* ── CARD CTA — full-width pill, colored by category ─────────
            • Active tools  → category-coloured pill, fills on hover
            • Coming soon   → amber pill "Em breve"
            • Disabled      → muted pill "Indisponível" */}
        {box.disabled ? (
          <span className="inline-flex items-center justify-center w-full gap-1 text-[11px] font-chunky uppercase tracking-wider px-2.5 py-1.5 rounded-md border border-zinc-200 bg-zinc-50 text-zinc-500 mt-1">
            {ctaDisabledLabel}
          </span>
        ) : comingSoon ? (
          <span className="inline-flex items-center justify-center w-full gap-1 text-[11px] font-chunky uppercase tracking-wider px-2.5 py-1.5 rounded-md border border-amber-300 bg-amber-50 text-amber-800 mt-1">
            {ctaSoonLabel}
          </span>
        ) : (
          <span className={`inline-flex items-center justify-center w-full gap-1 text-[11px] font-chunky uppercase tracking-wider px-2.5 py-1.5 rounded-md border transition-colors mt-1 ${acc.cta}`}>
            {ctaLabel}
            <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
          </span>
        )}
        {/* ── CASCADE HINT — "unlocked by" strip ─────────────────────
            Tiny row of plan chips below the CTA showing which paid
            plans grant access to this tool. Mirrors the cascade rule:
              • free  → all 4 plans light up (no gating)
              • maker → maker, pro, team
              • pro   → pro, team
              • team  → team only
              • draft → admin-only label, no chips
            Hidden for "free" tools to avoid noise (free tools are open
            to everyone, the hint adds nothing). Lit chips reuse the
            same color tokens as the top-right tier pill so the user
            instantly maps "this tool wants Pro" with the Pro plan card
            on /pricing. */}
        {!isDraftTool && isPaid && (
          <div className="mt-2 flex items-center gap-1.5 text-[9px] font-chunky uppercase tracking-[0.16em] text-zinc-500">
            <span className="shrink-0">{lang === 'en' ? 'Unlocks with' : (lang === 'es' ? 'Desbloquea con' : 'Desbloqueia em')}</span>
            <span className="flex items-center gap-1">
              {/* Free chip — only lit if the tool itself is free.
                  For paid tools (maker/pro/team) free is shown DIM
                  so the user sees the upgrade requirement at a glance. */}
              <span className={`px-1.5 py-px rounded ${tier === 'free' ? 'bg-zinc-900 text-cyan-300' : 'bg-zinc-100 text-zinc-300 line-through opacity-60'}`}>
                Free
              </span>
              <span className={`px-1.5 py-px rounded ${userTierAllows(tier, 'maker') ? 'bg-emerald-500 text-white' : 'bg-zinc-100 text-zinc-300 line-through opacity-60'}`}>
                Maker
              </span>
              <span className={`px-1.5 py-px rounded ${userTierAllows(tier, 'pro') ? 'bg-fuchsia-500 text-white' : 'bg-zinc-100 text-zinc-300 line-through opacity-60'}`}>
                Pro
              </span>
              <span className={`px-1.5 py-px rounded ${userTierAllows(tier, 'team') ? 'bg-indigo-500 text-white' : 'bg-zinc-100 text-zinc-300 line-through opacity-60'}`}>
                Team
              </span>
            </span>
          </div>
        )}
        {isDraftTool && (
          <div className="mt-2 text-[9px] font-chunky uppercase tracking-[0.18em] text-amber-700">
            {lang === 'en' ? 'Admin only — testing' : 'Apenas admin — em teste'}
          </div>
        )}
      </div>
    </article>
  );
  // Non-clickable wrappers for both legacy disabled tools and the
  // new "Em breve" badge — same UX, same tooltip, never navigates.
  if (box.disabled || comingSoon) {
    const tip = lang === 'en' ? 'Coming soon' : (lang === 'es' ? 'Próximamente' : 'Em breve');
    return <div title={tip} className="cursor-not-allowed select-none" aria-disabled="true">{content}</div>;
  }
  return <Link to={toolPath(box.slug)}>{content}</Link>;
}
